// Board of a view grouped by a taxonomy field — columns are stages (or labels) — or by the buckets of a date (R-U30: «Просрочено …
// Без даты»); cards drag between columns and within one; same query as the table. While a card is moved, its trace stays faded and a
// thin lit line marks its place (DESIGN.md «Доска»).

import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { formatMoney, rankOf, recordTitle, type FieldDef, type Id, type RecordObj, type ViewDef, avatarUrl, accountToday } from '@confdeck/core-shared';
import { FieldValue, TermChips } from '../fields/FieldValue.tsx';
import { bucketDate, dueBucket, DUE_BUCKETS, type DueBucket } from '../pipelines/due-buckets.ts';
import { pipelineColumn } from '../table/system-columns.tsx';
import { createRecord, moveRecord, setField } from '../sync/mutations.ts';
import { useMeta, useRecord, useSync, useTypesTick } from '../sync/react.tsx';
import { Icon } from '../ui/icons.tsx';
import { Avatar, Count, cx, useToast } from '../ui/primitives.tsx';
import { ghostTransform, startPointerDrag, type Place } from './drag.ts';
import { useFlip } from './flip.ts';
import { manualOrder, placeAt, sortedIndex } from './order.ts';
import s from './board.module.css';

const NONE = '__none';
const COLUMN_LIMIT = 200;

/** A card being moved: by the pointer (a ghost follows it) or by the keyboard (`keys`, the card stays and an insert line shows its place). */
interface Drag extends Place { id: Id; from: string; w: number; keys: boolean }

export function Board({ typeId, view, ids, onOpen, openId }: { typeId: Id; view: ViewDef; ids: Id[]; onOpen: (id: Id, e?: React.MouseEvent) => void; openId?: Id | null }) {
  const meta = useMeta();
  const client = useSync();
  const toast = useToast();
  // A move changes a card's stage or place without changing the query's list: the board follows the records themselves.
  const tick = useTypesTick([typeId]);
  const group = view.groupBy ? meta.field(typeId, view.groupBy.field) : undefined;
  const tax = group ? meta.taxonomy(group.options.taxonomyId) : undefined;
  // A date field grouped by when it is due: the columns are the buckets, a drop gives the record the bucket's date.
  const dates = view.groupBy?.dates === 'due' && (group?.fieldType === 'date' || group?.fieldType === 'datetime');
  const today = accountToday(meta);
  const fold = view.groupBy?.finals === 'fold';
  const pipelineId = view.groupBy?.pipelineId ?? (tax?.style === 'pipeline' ? meta.children(null, tax.id)[0]?.id : undefined);
  const columns = useMemo(() => {
    if (dates) return DUE_BUCKETS.map((b) => ({ id: b.key as string, name: b.label, color: b.color, final: null }));
    if (!tax) return [];
    return tax.style === 'pipeline' ? meta.children(pipelineId ?? null, tax.id) : meta.terms(tax.id);
  }, [dates, tax, pipelineId, meta, meta.all.size]);
  /** A sorted view orders cards by its sort; otherwise the order is the people's own (manual ranks). */
  const sorted = view.sort.length > 0;
  const rank = (id: Id) => rankOf(client.store.record(id)?.data ?? {});

  // Bucket the query result by term; records of another pipeline stay out of this board.
  const buckets = useMemo(() => {
    const m = new Map<string, Id[]>(columns.map((c) => [c.id, []]));
    m.set(NONE, []);
    if (!group) return m;
    for (const id of ids) {
      const r = client.store.record(id);
      const v = r?.data[group.key];
      if (dates) { m.get(dueBucket(v, today))!.push(id); continue; }
      const first = Array.isArray(v) ? v[0] : v;
      const bucket = typeof first === 'string' ? m.get(first) : m.get(NONE);
      if (bucket) bucket.push(id);
      else if (tax?.style !== 'pipeline') m.get(NONE)!.push(id);
    }
    if (!sorted) for (const [k, list] of m) m.set(k, manualOrder(list, rank));
    return m;
  }, [ids, columns, group, client.store, tax, sorted, tick, dates, today]);

  const moneyField = meta.fields(typeId).find((f) => f.fieldType === 'money');
  const cardFields = view.columns.filter((c) => !c.hidden && c.field !== meta.type(typeId)?.titleField && c.field !== group?.key)
    .map((c): CardField | null => { const f = meta.field(typeId, c.field); if (f && !f.archivedAt) return { key: c.field, field: f }; const pc = pipelineColumn(meta, typeId, c.field); return pc ? { key: c.field, pipeline: pc.base } : null; })
    .filter((x): x is CardField => !!x);

  const [drag, setDrag] = useState<Drag | null>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  const ghostRef = useRef<HTMLDivElement>(null);
  const ghostAt = useRef({ x: 0, y: 0 });
  const dropRef = useRef<{ id: string; rect: DOMRect; until: number } | null>(null);
  const refocus = useRef<Id | null>(null);
  const layoutRev = useRef(0);
  useLayoutEffect(() => { layoutRev.current++; });
  useFlip(boardRef, `${drag?.over}|${drag?.index}|${drag?.id}|${tick}|${ids.length}`, dropRef);
  // A keyboard move keeps its insert line in view and the moved card focused after the drop (it may land in another column).
  useLayoutEffect(() => {
    if (drag?.keys) {
      // Only the column scrolls to the line (and the board sideways to the column): the page around stays where it is.
      const line = boardRef.current?.querySelector<HTMLElement>('[data-insert]');
      const col = line?.parentElement;
      if (line && col) {
        if (line.offsetTop < col.scrollTop || line.offsetTop > col.scrollTop + col.clientHeight) col.scrollTop = line.offsetTop - col.clientHeight / 2;
        const board = boardRef.current!, sec = col.parentElement as HTMLElement;
        if (sec.offsetLeft < board.scrollLeft || sec.offsetLeft + sec.offsetWidth > board.scrollLeft + board.clientWidth) board.scrollLeft = sec.offsetLeft - board.clientWidth / 2 + sec.offsetWidth / 2;
      }
    }
    if (!drag && refocus.current) {
      boardRef.current?.querySelector<HTMLElement>(`[data-id="${CSS.escape(refocus.current)}"]`)?.focus({ preventScroll: false });
      refocus.current = null;
    }
  });

  /** A managed status (orders, tickets) changes through record actions, not by dragging (rule 10); a managed stage with a move command is dragged. */
  const movable = !!group && (!group.options.managed || (!dates && !!group.options.moveCommand));
  const position = useMemo(() => new Map(ids.map((x, i) => [x, i])), [ids]);
  const othersIn = (col: string, id: Id) => (buckets.get(col) ?? []).filter((x) => x !== id);

  /** The running pointer gesture: one at a time; the board going away removes its window listeners. */
  const gesture = useRef<(() => void) | null>(null);
  useEffect(() => () => gesture.current?.(), []);

  const startDrag = (id: Id, e: React.PointerEvent) => {
    if (e.button !== 0 || !movable || drag || gesture.current) return;
    let from: string | null = null;
    gesture.current = startPointerDrag(e, id, {
      board: boardRef.current, ghost: () => ghostRef.current, rev: () => layoutRev.current, buckets, ids, sorted, none: NONE,
      onStart: (p, rect, at) => { from = p.over ?? NONE; ghostAt.current = at; setDrag({ id, from, ...p, w: rect.width, keys: false }); },
      onPlace: (p) => setDrag((d) => (d ? { ...d, ...p } : d)),
      onDrop: (p, ghost) => {
        drop(id, from, p);
        if (ghost) dropRef.current = { id, rect: ghost, until: performance.now() + 400 };
        setDrag(null);
      },
      onCancel: () => setDrag(null),
      onEnd: () => { gesture.current = null; },
    });
  };

  /** Keyboard move: Space lifts the focused card, arrows choose the place, Space or Enter drops it there, Esc puts it back. */
  const onCardKey = (id: Id, e: React.KeyboardEvent) => {
    if (!drag) {
      if (e.key === 'Enter') onOpen(id);
      else if (e.key === ' ' && movable) {
        e.preventDefault();
        const from = (e.currentTarget.parentElement as HTMLElement).dataset.cards ?? NONE;
        setDrag({ id, from, over: from, index: (buckets.get(from) ?? []).indexOf(id), w: 0, keys: true });
      }
      return;
    }
    if (!drag.keys || drag.id !== id) return;
    const ring = cols.map((c) => c.id).filter((c) => c !== NONE);
    const at = (col: string, index: number) => sorted ? sortedIndex(buckets.get(col) ?? [], id, (x) => position.get(x) ?? 0) : Math.max(0, Math.min(index, othersIn(col, id).length));
    const go = (over: string, index: number) => { e.preventDefault(); setDrag({ ...drag, over, index: at(over, index) }); };
    const over = drag.over ?? drag.from;
    const index = drag.index ?? 0;
    if (e.key === 'ArrowUp' && !sorted) go(over, index - 1);
    else if (e.key === 'ArrowDown' && !sorted) go(over, index + 1);
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      const i = ring.indexOf(over) + (e.key === 'ArrowLeft' ? -1 : 1);
      if (i >= 0 && i < ring.length) go(ring[i]!, index);
      else e.preventDefault();
    } else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      dropRef.current = { id, rect: e.currentTarget.getBoundingClientRect(), until: performance.now() + 400 };
      refocus.current = id;
      drop(id, drag.from, drag);
      setDrag(null);
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      if (e.key === 'Escape') e.preventDefault();
      setDrag(null);
    }
  };

  /** Writes a drop: the stage when the column changed, the manual place unless the view is sorted. */
  const drop = (id: Id, from: string | null, { over: col, index }: Place) => {
    const rec = client.store.record(id);
    if (!rec || !group || !from || !col || col === NONE || index === null) return;
    const stageChanged = col !== from;
    const list = (buckets.get(col) ?? []).filter((x) => x !== id);
    if (!stageChanged && (sorted || (buckets.get(col) ?? []).indexOf(id) === index)) return;
    // A bucket gives its first day; «Просрочено» gives none — a date in the past is not planned (R-U30).
    const day = dates ? bucketDate(col as DueBucket, today) : undefined;
    if (dates && stageChanged && day === undefined) { toast({ kind: 'error', text: 'Срок в прошлом не ставится: перенесите карточку на сегодня или позже' }); return; }
    const value = dates ? day : group.options.multiple ? [col] : col;
    const rev = client.store.confirmedGet(rec.id)?.rev;
    const fail = (r: { ok: boolean; error?: { message: string } }) => { if (!r.ok) toast({ kind: 'error', text: r.error?.message ?? 'Не удалось перенести карточку' }); };
    if (sorted) {
      if (group.options.moveCommand) void client.run(group.options.moveCommand, { id: rec.id, status: col }).then(fail);
      else setField(client, rec, group.key, value, { rev });
      return;
    }
    const place = placeAt(list, index, rank);
    // A managed stage moves by the app's command first (it checks the move); the place follows once the stage is taken.
    if (stageChanged && group.options.moveCommand) {
      void client.run(group.options.moveCommand, { id: rec.id, status: col }).then((r) => {
        fail(r);
        const now = client.store.record(id);
        if (r.ok && now) moveRecord(client, now, place);
      });
      return;
    }
    moveRecord(client, rec, { ...place, ...(stageChanged ? { data: { [group.key]: value }, rev } : {}) });
  };

  if (!group || (!tax && !dates)) return <div className={s.empty}>Для доски нужна группировка по таксономии — выберите поле в настройках вида.</div>;

  const cols = [...columns.map((c) => ({ id: c.id, name: c.name, color: c.color, final: c.final })), ...(buckets.get(NONE)!.length ? [{ id: NONE, name: 'Без этапа', color: 'gray', final: null }] : [])];
  const title = (id: Id) => { const r = client.store.record(id); return r ? recordTitle(r, meta) : ''; };

  return (
    <div className={cx(s.board, drag && !drag.keys && s.dragging)} ref={boardRef}>
      {cols.map((c) => {
        const all = buckets.get(c.id) ?? [];
        const others = drag ? all.filter((id) => id !== drag.id) : all;
        const sum = moneyField ? all.reduce((a, id) => a + (Number(client.store.record(id)?.data[moneyField.key]) || 0), 0) : 0;
        // A folded final stage (the board of a pipeline, R-U31) takes cards dropped on it but does not show the closed ones.
        const folded = fold && !!c.final;
        const shown = folded ? [] : all.slice(0, COLUMN_LIMIT);
        // The card goes back where it was: no line, its faded trace marks the place.
        const stays = drag?.from === c.id && drag.over === c.id && (sorted || all.indexOf(drag.id) === drag.index);
        const at = drag?.over === c.id && c.id !== NONE && drag.index !== null && !stays ? Math.min(drag.index, others.length) : -1;
        const insert = at < 0 ? null : <div key="insert" data-insert className={cx(s.insert, others.length === 0 && s.insertEmpty)} aria-hidden><i /></div>;
        const items: React.ReactNode[] = [];
        let k = 0;
        for (const id of shown) {
          const lifted = drag?.id === id;
          if (!lifted && k++ === at) items.push(insert);
          items.push(<Card key={id} id={id} fields={cardFields} open={openId === id} lifted={lifted} onOpen={onOpen} onPointerDown={(e) => startDrag(id, e)} onKey={onCardKey} />);
        }
        if (at >= k) items.push(insert);
        return (
          <section key={c.id} data-col={c.id} className={cx(s.col, drag?.over === c.id && c.id !== NONE && s.colOver, c.final === 'lost' && s.colLost, folded && s.colFolded)} aria-label={c.name}>
            <header className={s.colHead}>
              <span className={s.colDot} style={{ background: `var(--c-${c.color}-dot)` }} />
              <span className={s.colName}>{c.name}</span>
              <Count className={s.colCount} value={all.length} />
              {moneyField && sum > 0 && <Count className={s.colSum} value={formatMoney(sum, moneyField.options.currency)} />}
            </header>
            <div className={s.cards} data-cards={c.id}>
              {items}
              {folded && <div className={s.more}>Закрытые не показаны — они в списке</div>}
              {!folded && all.length > COLUMN_LIMIT && <div className={s.more}>Ещё {all.length - COLUMN_LIMIT} — уточните фильтр или откройте таблицу</div>}
              {c.id !== NONE && movable && !dates && !folded && (
                <button type="button" className={s.add} onClick={() => {
                  const titleField = meta.type(typeId)?.titleField ?? 'name';
                  // A managed stage is not written by a generic create: the card is created first, then moved by the field's command.
                  // A new card has no manual place yet, so it shows at the top of its column.
                  const managed = group.options.managed;
                  const id = createRecord(client, typeId, { [titleField]: 'Новая запись', ...(managed ? {} : { [group.key]: group.options.multiple ? [c.id] : c.id }) });
                  if (managed && group.options.moveCommand) void client.run(group.options.moveCommand, { id, status: c.id }).then((r) => { if (!r.ok) toast({ kind: 'error', text: r.error.message }); });
                  onOpen(id);
                }}><Icon name="plus" size={14} />Добавить</button>
              )}
            </div>
          </section>
        );
      })}
      {drag && sorted && <div className={s.hint} role="status">Порядок карточек задаёт сортировка вида — карточка встанет на своё место по ней</div>}
      <div className="sr-only" role="status" aria-live="assertive">
        {drag?.keys ? `«${title(drag.id)}»: ${cols.find((c) => c.id === drag.over)?.name ?? ''}, место ${(drag.index ?? 0) + 1}. Стрелки — выбрать место, пробел — положить, Esc — отменить` : ''}
      </div>
      {drag && !drag.keys && <DragGhost ref={ghostRef} id={drag.id} at={ghostAt.current} w={drag.w} fields={cardFields} />}
    </div>
  );
}

const Card = memo(function Card({ id, fields, open, lifted, onOpen, onPointerDown, onKey }: {
  id: Id; fields: CardField[]; open: boolean; lifted: boolean; onOpen: (id: Id, e?: React.MouseEvent) => void;
  onPointerDown: (e: React.PointerEvent) => void; onKey: (id: Id, e: React.KeyboardEvent) => void;
}) {
  const rec = useRecord(id);
  if (!rec) return null;
  return (
    <article data-id={id} className={cx(s.card, open && s.cardOpen, lifted && s.cardLifted)} onPointerDown={onPointerDown} onClick={(e) => onOpen(id, e)} tabIndex={0}
      aria-roledescription="карточка, пробел — перенести" onKeyDown={(e) => onKey(id, e)}>
      <CardBody rec={rec} fields={fields} />
    </article>
  );
});

/** A field of a card: its own value, or the pipeline of a stage field (`stage:pipeline`). */
interface CardField { key: string; field?: FieldDef; pipeline?: FieldDef }

function CardBody({ rec, fields }: { rec: RecordObj; fields: CardField[] }) {
  const meta = useMeta();
  const owner = meta.user(rec.ownerId);
  return (
    <>
      <div className={s.cardTitle}>{recordTitle(rec, meta)}</div>
      {fields.slice(0, 4).map(({ key, field: f, pipeline }) => {
        if (pipeline) { const stage = meta.term(String(rec.data[pipeline.key] ?? '')); return stage?.parentId ? <div key={key} className={s.cardField}><TermChips ids={stage.parentId} /></div> : null; }
        if (!f) return null;
        const empty = f.fieldType !== 'relation' && (rec.data[f.key] == null || rec.data[f.key] === '');
        return empty ? null : <div key={key} className={s.cardField}><FieldValue rec={rec} field={f} /></div>;
      })}
      {owner && <div className={s.cardFoot}><Avatar name={owner.name} color={owner.color} src={avatarUrl(owner)} size={18} /></div>}
    </>
  );
}

/** The card in the hand: the outer layer follows the pointer, the card inside lifts (shadow, tilt, scale) once picked up. */
function DragGhost({ ref, id, at, w, fields }: { ref: React.Ref<HTMLDivElement>; id: Id; at: { x: number; y: number }; w: number; fields: CardField[] }) {
  const rec = useRecord(id);
  if (!rec) return null;
  return (
    <div ref={ref} className={s.ghost} style={{ width: w, transform: ghostTransform(at) }} aria-hidden>
      <article className={cx(s.card, s.ghostCard)}><CardBody rec={rec} fields={fields} /></article>
    </div>
  );
}
